import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    Paper,
    Chip,
    Skeleton,
    IconButton,
    Tooltip,
    InputLabel,
    ButtonGroup,
    Button,
    Alert,
} from "@mui/material";
import { TrendingUp, TrendingDown, Download, Info, Calendar, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { getStockFinancials, getStocksScreenerRatioTypes } from "../../../api/stocks_api";
import { isShareCountKey, normalizeShareCount } from "../../../config/statements";
import * as XLSX from "xlsx";

/* ================= CONSTANTS ================= */

/* Fallback only. Which period is "current" comes from the payload's
   `is_projected` flag (see periodTypes) — this constant is used only for
   responses old enough not to carry that flag. */
const CURRENT_YEAR = 2025;

// ✅ Use browser locale for number formatting (3.3.3)
const NUMBER_LOCALE = typeof navigator !== "undefined" ? navigator.language : "en-US";

const FINANCIAL_TYPE_BY_TAB = {
    0: "income_statement",
    1: "balance_sheet",
    2: "cash_flow",
    3: "ratios",
};

const TAB_INFO = {
    0: "Revenue, expenses, and profitability metrics",
    1: "Assets, liabilities, and equity positions",
    2: "Operating, investing, and financing activities",
    3: "Key financial ratios and performance indicators",
};

const LABEL_OVERRIDES = {
    other_income_or_expense_net: "Other income expense",
    eps: "EPS",
    ebitda: "EBITDA",
    ebit: "EBIT",
    accumulated_other_comprehensive_income_loss: "Other equity adjustments",
    other_working_capital: "Other working capital changes",
    other_operating_activities: "Other Operating Activities",
    selling_general_and_administrative_expenses: "Selling, General & Administrative",
    other_operating_expenses: "Other Operating Expense",
    other_income_expense: "Other Income/(Expense)",
    bottom_line_net_income: "Bottom Line Net Income",
};

/* ================= KEY ORDERS ================= */

const INCOME_STATEMENT_KEY_ORDER = [
    "revenue", "cost_of_revenue", "gross_profit",
    "selling_general_and_administrative_expenses", "other_operating_expenses",
    "depreciation_amortization", "operating_expenses", "operating_income",
    "interest_income", "interest_expense", "other_income_expense",
    "income_before_tax", "income_tax_expense", "net_income", "minority_interest",
    "bottom_line_net_income", "ebitda", "eps_basic", "eps_diluted",
    "shares_outstanding", "shares_outstanding_diluted", "gross_margin",
    "operating_margin", "ebitda_margin", "net_margin",
];

const INCOME_STATEMENT_KEY_MAP = {
    cost_of_goods_sold: "cost_of_revenue",
    operating_income_ebit: "operating_income",
    eps: "eps_basic",
    shares_outstanding_m: "shares_outstanding",
};

const BALANCE_SHEET_KEY_ORDER = [
    "cash_and_equivalents", "short_term_investments", "accounts_receivable",
    "inventory", "other_current_assets", "total_current_assets",
    "property_plant_equipment", "intangible_assets", "goodwill",
    "long_term_investments", "other_non_current_assets", "total_non_current_assets",
    "total_assets", "accounts_payable", "short_term_debt",
    "other_current_liabilities", "total_current_liabilities", "long_term_debt",
    "other_non_current_liabilities", "total_non_current_liabilities",
    "total_liabilities", "common_stock", "retained_earnings",
    "accumulated_other_comprehensive_income_loss", "total_stockholders_equity",
    "minority_interest", "total_equity",
];

const CASH_FLOW_KEY_ORDER = [
    "net_income", "depreciation_amortization", "stock_based_compensation",
    "inventory_change", "accounts_payable_change", "accounts_receivable_change",
    "other_working_capital", "change_in_working_capital", "other_operating_activities",
    "operating_cash_flow", "capital_expenditure", "investment_purchases",
    "investment_sales", "acquisitions", "other_investing_activities",
    "investing_cash_flow", "debt_repayment", "debt_issued", "dividends_paid",
    "stock_repurchased", "stock_issued", "other_financing_activities",
    "financing_cash_flow", "net_change_in_cash", "free_cash_flow",
];

/* ================= UTILITY FUNCTIONS ================= */

const isEPSKey = (key) =>
    key.toLowerCase().includes("eps") ||
    key.toLowerCase().includes("shares_outstanding");

const prettifyKey = (key) => {
    if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
    return key
        .replace(/_/g, " ")
        .replace(/(^|\s)(ebitda|ebt|eps|roe|roa|ebit)(\s|$)/gi, (_, p1, p2, p3) =>
            `${p1}${p2.toUpperCase()}${p3}`
        )
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

const isPercentField = (key) => {
    const percentKeywords = [
        "margin", "yield", "return", "roe", "roa", "growth",
        "payout", "tax_rate", "ebt_per_ebit", "net_income_per_ebt",
    ];
    return percentKeywords.some(kw => key.includes(kw));
};

const calculateChange = (current, previous) => {
    if (!current || !previous || current === "—" || previous === "—") return null;
    const curr = parseFloat(current.replace(/[^0-9.-]/g, ""));
    const prev = parseFloat(previous.replace(/[^0-9.-]/g, ""));
    if (isNaN(curr) || isNaN(prev) || prev === 0) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
};

/**
 * Which period is CURRENT, which are ESTIMATES.
 *
 * The API states this per row with `is_projected` and that flag is the only
 * thing to trust: the running fiscal year comes back `is_projected: false`
 * while the years after it come back `true`, so FY2026 can be an actual even
 * though FY2027 is a projection. Current = the LAST row flagged false;
 * anything flagged true is an estimate.
 *
 * Older responses omit the flag entirely — those fall back to comparing the
 * fiscal year against CURRENT_YEAR (annual) or picking the latest period
 * (quarterly), which is what this did before.
 *
 * @param {Array<object>} periods  rows in the order they will be rendered
 * @returns {Array<"current"|"estimate"|"past">}
 */
const periodTypes = (periods) => {
    const hasFlag = periods.some((p) => typeof p?.is_projected === "boolean");

    if (hasFlag) {
        /* last actual in CHRONOLOGICAL terms — the rendered order may be
           either direction, so compare fiscal years rather than indexes */
        let currentIdx = -1;
        let currentYear = -Infinity;
        periods.forEach((p, idx) => {
            if (p?.is_projected) return;
            const y = Number(p?.fiscal_year) || 0;
            const q = Number(p?.fiscal_quarter) || 0;
            const stamp = y * 10 + q;
            if (stamp > currentYear) {
                currentYear = stamp;
                currentIdx = idx;
            }
        });
        return periods.map((p, idx) =>
            p?.is_projected ? "estimate" : idx === currentIdx ? "current" : "past"
        );
    }

    const isQuarterly = periods.some((p) => p?.period_type === "Q");
    if (isQuarterly) {
        const latestIdx = periods.reduce((bestIdx, item, idx, arr) => {
            const best = arr[bestIdx];
            if (item.fiscal_year > best.fiscal_year) return idx;
            if (item.fiscal_year === best.fiscal_year && item.fiscal_quarter > best.fiscal_quarter) return idx;
            return bestIdx;
        }, 0);
        return periods.map((_, idx) => (idx === latestIdx ? "current" : "past"));
    }

    return periods.map((item) => {
        if (item.fiscal_year > CURRENT_YEAR) return "estimate";
        if (item.fiscal_year === CURRENT_YEAR) return "current";
        return "past";
    });
};

/* Same rule, for a single row (chart points). */
const isEstimatePeriod = (item) =>
    typeof item?.is_projected === "boolean"
        ? item.is_projected
        : item?.fiscal_year > CURRENT_YEAR;

const formatQuarterLabel = (item) => {
    if (item.period_type === "Q" && item.fiscal_quarter) {
        return `Q${item.fiscal_quarter} ${item.fiscal_year}`;
    }
    return `FY ${item.fiscal_year}`;
};

/* ================= HIGHLIGHT CONFIG ================= */

const HIGHLIGHT_KEYS = {
    income_statement: ["revenue", "gross_profit", "net_income"],
    balance_sheet: [
        "total_current_assets", "total_non_current_assets", "total_assets",
        "total_current_liabilities", "total_non_current_liabilities",
        "total_liabilities", "total_stockholders_equity", "total_equity",
    ],
    cash_flow: [
        "operating_cash_flow", "investing_cash_flow",
        "free_cash_flow", "financing_cash_flow",
    ],
};

/* ================= DATA TRANSFORMATION ================= */

const transformFinancialData = (data, financialType, sortDirection = "desc") => {
    if (!Array.isArray(data) || data.length === 0) {
        return { columns: [], rows: [], scale: null, columnTypes: [], rawData: [] };
    }

    const sorted =
        financialType === "ratios"
            ? [...data]
            : [...data].sort((a, b) => {
                const yearDiff = b.fiscal_year - a.fiscal_year;
                if (yearDiff !== 0) return sortDirection === "desc" ? yearDiff : -yearDiff;
                const qDiff = (b.fiscal_quarter ?? 0) - (a.fiscal_quarter ?? 0);
                return sortDirection === "desc" ? qDiff : -qDiff;
            });

    const INCOME_KEY_MAP = {
        cost_of_goods_sold: "cost_of_revenue",
        operating_income_ebit: "operating_income",
        eps: "eps_basic",
        shares_outstanding_m: "shares_outstanding",
    };

    const normalizedData = sorted.map((item) => {
        const newItem = { ...item };
        if (financialType === "income_statement") {
            Object.entries(INCOME_KEY_MAP).forEach(([apiKey, uiKey]) => {
                if (newItem[apiKey] !== undefined) newItem[uiKey] = newItem[apiKey];
            });
        }
        return newItem;
    });

    const numericValues = normalizedData.flatMap((item) =>
        Object.entries(item)
            .filter(([k, v]) => typeof v === "number" && !isEPSKey(k))
            .map(([, v]) => Math.abs(v))
    );

    let scale = null;
    if (financialType !== "ratios") {
        const maxValue = Math.max(...numericValues, 0);
        scale =
            maxValue >= 1_000_000
                ? { divisor: 1_000_000, label: "millions" }
                : { divisor: 1_000, label: "thousands" };
    }

    const columns =
        financialType === "ratios"
            ? ["TTM Q4 2025"]
            : normalizedData.map((item) => formatQuarterLabel(item));

    const columnTypes = periodTypes(normalizedData);

    let orderedKeys = [];
    if (financialType === "income_statement") orderedKeys = [...INCOME_STATEMENT_KEY_ORDER];
    else if (financialType === "balance_sheet") orderedKeys = [...BALANCE_SHEET_KEY_ORDER];
    else if (financialType === "cash_flow") orderedKeys = [...CASH_FLOW_KEY_ORDER];
    else if (financialType === "ratios") {
        const firstItem = normalizedData[0] || {};
        orderedKeys = Object.keys(firstItem).filter(
            key =>
                typeof firstItem[key] === "number" &&
                !["id", "stock_id", "created_at", "updated_at", "fetched_at"].includes(key)
        );
    }

    const rows = orderedKeys.map((key) => {
        const isPercent = isPercentField(key);
        const values = normalizedData.map((item) => {
            const raw = item[key];
            if (raw === null || raw === undefined) return "—";
            /* Share counts arrive in mixed units (billions / millions /
               absolute) — normalise, then show them in the table's own scale
               so they read like every other line. */
            if (isShareCountKey(key)) {
                const num = typeof raw === "number" ? raw : parseFloat(raw);
                if (isNaN(num)) return "—";
                const shares = normalizeShareCount(num, { period: item, key });
                const scaled = scale ? shares / scale.divisor : shares;
                return scaled.toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 });
            }
            if (isEPSKey(key)) {
                const num = typeof raw === "number" ? raw : parseFloat(raw);
                if (isNaN(num)) return "—";
                return num.toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 });
            }
            if (typeof raw !== "number") return "—";
            if (isPercent) {
                return `${(raw * 100).toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 })}%`;
            }
            if (key.includes("ratios") || key.includes("debt_to_equity") || key.includes("debt_to_assets")) {
                return raw.toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 });
            }
            const scaled = scale ? raw / scale.divisor : raw;
            return scaled.toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 });
        });

        return {
            label: prettifyKey(key),
            key,
            values,
            isPercent,
            isHighlighted: HIGHLIGHT_KEYS[financialType]?.includes(key) || false,
        };
    });

    return { columns, rows, scale, columnTypes, rawData: normalizedData };
};

/* ================= SKELETON ================= */

const TableSkeleton = () => (
    <Box sx={{ p: 3 }}>
        {Array.from({ length: 8 }).map((_, i) => (
            <Box key={i} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
                {[25, 15, 15, 15, 15].map((w, j) => (
                    <Skeleton
                        key={j}
                        variant="text"
                        width={`${w}%`}
                        height={32}
                        sx={{
                            bgcolor: "var(--color-surface-hover)",
                            "&::after": {
                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)",
                            },
                        }}
                    />
                ))}
            </Box>
        ))}
    </Box>
);

/* ================= MAIN COMPONENT ================= */

const Financials = ({ stock }) => {
    const [tab, setTab] = useState(0);
    const [period, setPeriod] = useState("annual");
    const [sortDirection, setSortDirection] = useState("desc");
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [columnTypes, setColumnTypes] = useState([]);
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unitNote, setUnitNote] = useState("");
    const [viewMode, setViewMode] = useState("table");
    const [baseRows, setBaseRows] = useState([]);
    const [ratioCategories, setRatioCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [fetchError, setFetchError] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);

    useEffect(() => { fetchRatioCategories(); }, []);

    const fetchRatioCategories = async () => {
        try {
            const res = await getStocksScreenerRatioTypes();
            if (res.success || res.response_status === "SUCCESS") {
                setRatioCategories(res.data || res.response_data || []);
            }
        } catch (err) {
            console.error("Failed to fetch ratio categories", err);
        }
    };

    useEffect(() => {
        if (!stock?.ticker) return;
        const fetchData = async () => {
            setLoading(true);
            setFetchError(false);
            const financialType = FINANCIAL_TYPE_BY_TAB[tab];
            const apiPeriod = tab === 3 ? "TTM Q4 2025" : period;
            const res = await getStockFinancials(stock.ticker, apiPeriod, financialType);

            if (res?.success) {
                const payload = res.data?.response_data;
                let rawDataArray = [];
                if (tab === 0) rawDataArray = payload?.income_statement;
                if (tab === 1) rawDataArray = payload?.balance;
                if (tab === 2) rawDataArray = payload?.cash_flow;
                if (tab === 3) rawDataArray = payload?.ratios;

                const { columns, rows, scale, columnTypes, rawData: sortedRawData } =
                    transformFinancialData(rawDataArray, financialType, sortDirection);

                setColumns(columns);
                setBaseRows(rows);
                setColumnTypes(columnTypes);
                setRawData(sortedRawData);
                setUnitNote(scale?.label ?? "");
                setLastFetched(new Date());
            } else {
                setFetchError(true);
                /* Keep stale data if we have it — don't clear on error (6.1.3) */
                if (columns.length === 0) {
                    setColumns([]); setRows([]); setColumnTypes([]); setRawData([]);
                }
            }
            setLoading(false);
        };
        fetchData();
    }, [stock?.ticker, tab, period, sortDirection]);

    useEffect(() => {
        if (tab !== 3) { setRows(baseRows); return; }
        if (!selectedCategoryId) { setRows(baseRows); return; }
        const category = ratioCategories.find(c => c.id === selectedCategoryId);
        if (!category) { setRows(baseRows); return; }
        const allowedKeys = category.ratios.map(r => r.ratio_name);
        setRows(baseRows.filter(row => allowedKeys.includes(row.key)));
    }, [selectedCategoryId, baseRows, tab, ratioCategories]);

    const prepareChartData = () => {
        if (!rawData || rawData.length === 0) return [];

        const metricsByTab = {
            0: ["revenue", "net_income", "gross_profit", "operating_income", "ebitda"],
            1: ["total_assets", "total_liabilities", "total_stockholders_equity"],
            2: ["operating_cash_flow", "investing_cash_flow", "financing_cash_flow", "free_cash_flow"],
            3: ["gross_profit_margin", "operating_profit_margin", "net_profit_margin", "return_on_equity"],
        };

        const keyMetrics = metricsByTab[tab] || metricsByTab[0];
        const isRatiosTab = tab === 3;

        const chronological = [...rawData].sort((a, b) => {
            const yearDiff = a.fiscal_year - b.fiscal_year;
            if (yearDiff !== 0) return yearDiff;
            return (a.fiscal_quarter ?? 0) - (b.fiscal_quarter ?? 0);
        });
        const finalData = sortDirection === "desc" ? [...chronological].reverse() : chronological;

        /* The reference line sits on the last ACTUAL year (per is_projected). */
        const currentChartYear = chronological.reduce(
            (best, item) => (isEstimatePeriod(item) ? best : Math.max(best, Number(item.fiscal_year) || 0)),
            -Infinity
        );

        return finalData.map((item) => {
            const dataPoint = {
                period: formatQuarterLabel(item),
                year: item.fiscal_year,
                isEstimate: isEstimatePeriod(item),
                isCurrent: !isEstimatePeriod(item) && item.fiscal_year === currentChartYear,
            };

            keyMetrics.forEach(metric => {
                if (item[metric] !== undefined && item[metric] !== null) {
                    dataPoint[metric] = isRatiosTab || isPercentField(metric)
                        ? parseFloat((item[metric] * 100).toFixed(2))
                        : parseFloat((item[metric] / 1_000_000).toFixed(2));
                }
            });

            return dataPoint;
        });
    };

    const chartData = prepareChartData();

    /* The last actual period, per is_projected — labels the orange reference
       line and the legend, so neither says a hardcoded year any more. */
    const currentPoint = chartData.find((d) => d.isCurrent);
    const currentYearLabel = currentPoint?.year ?? CURRENT_YEAR;

    const getMetricColor = (_, index) => {
        const palette = ["#60a5fa", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
        return palette[index % palette.length];
    };

    const getActiveMetrics = () => ({
        0: ["revenue", "net_income", "gross_profit"],
        1: ["total_assets", "total_liabilities", "total_stockholders_equity"],
        2: ["operating_cash_flow", "investing_cash_flow", "financing_cash_flow", "free_cash_flow"],
    }[tab] || []);

    const activeMetrics = getActiveMetrics();

    const exportToExcel = () => {
        if (rows.length === 0) return;
        const sheetData = [["Metric", ...columns], ...rows.map(row => [row.label, ...row.values])];
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Financials");
        XLSX.writeFile(wb, `${stock.ticker}_financials_${FINANCIAL_TYPE_BY_TAB[tab]}.xlsx`);
    };

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Paper
                elevation={0}
                sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: "var(--radius-xl)",
                    overflow: "hidden",
                    boxShadow: "var(--shadow-md)",
                }}
            >
                {/* Stale data / error banner (6.1.3) */}
                {fetchError && columns.length > 0 && (
                    <Alert
                        severity="warning"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setFetchError(false);
                                    setLoading(true);
                                    const financialType = FINANCIAL_TYPE_BY_TAB[tab];
                                    const apiPeriod = tab === 3 ? "TTM Q4 2025" : period;
                                    getStockFinancials(stock.ticker, apiPeriod, financialType).then((res) => {
                                        if (res?.success) {
                                            const payload = res.data?.response_data;
                                            let rawDataArray = [];
                                            if (tab === 0) rawDataArray = payload?.income_statement;
                                            if (tab === 1) rawDataArray = payload?.balance;
                                            if (tab === 2) rawDataArray = payload?.cash_flow;
                                            if (tab === 3) rawDataArray = payload?.ratios;
                                            const result = transformFinancialData(rawDataArray, financialType, sortDirection);
                                            setColumns(result.columns);
                                            setBaseRows(result.rows);
                                            setColumnTypes(result.columnTypes);
                                            setRawData(result.rawData);
                                            setUnitNote(result.scale?.label ?? "");
                                            setLastFetched(new Date());
                                        } else {
                                            setFetchError(true);
                                        }
                                        setLoading(false);
                                    });
                                }}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        }
                        sx={{
                            bgcolor: "var(--color-surface-hover)",
                            color: "var(--color-text-primary)",
                            borderBottom: "1px solid var(--color-border-primary)",
                            borderRadius: 0,
                            "& .MuiAlert-icon": { color: "var(--color-warning, #f59e0b)" },
                        }}
                    >
                        Unable to refresh data. Showing previously loaded results
                        {lastFetched ? ` from ${lastFetched.toLocaleTimeString()}` : ""}.
                    </Alert>
                )}

                {/* ── Header ── */}
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2, borderBottom: "1px solid var(--color-border-primary)" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 2 }}>
                        <Box>
                            <Typography variant="h6" fontWeight={700} mb={0.5} color="var(--color-text-primary)">
                                Financial Statements
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="paragraphV2" color="var(--color-text-muted)">
                                    {TAB_INFO[tab]}
                                </Typography>
                                {unitNote && (
                                    <Typography variant="paragraphV2" sx={{ color: "var(--color-text-muted)", fontWeight: 500, display: "inline-block" }}>
                                        All numbers are presented in{" "}
                                        <b style={{ color: "var(--color-text-secondary)" }}>{unitNote}</b>{" "}
                                        unless otherwise stated.
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            <ButtonGroup size="small" sx={{ mr: 1 }}>
                                <Button
                                    onClick={() => setViewMode("table")}
                                    variant={viewMode === "table" ? "contained" : "outlined"}
                                    sx={{
                                        borderColor: "var(--color-border-primary)",
                                        color: viewMode === "table" ? "#ffffff" : "var(--color-text-muted)",
                                        bgcolor: viewMode === "table" ? "var(--color-primary)" : "transparent",
                                        "&:hover": {
                                            bgcolor: viewMode === "table" ? "var(--color-primary-dark)" : "var(--color-surface-hover)",
                                            borderColor: "var(--color-border-secondary)",
                                        },
                                        textTransform: "none",
                                        fontSize: "var(--font-size-xs)",
                                    }}
                                >
                                    Table
                                </Button>
                                {tab !== 3 && (
                                    <Button
                                        onClick={() => setViewMode("chart")}
                                        variant={viewMode === "chart" ? "contained" : "outlined"}
                                        sx={{
                                            borderColor: "var(--color-border-primary)",
                                            color: viewMode === "chart" ? "#ffffff" : "var(--color-text-muted)",
                                            bgcolor: viewMode === "chart" ? "var(--color-primary)" : "transparent",
                                            "&:hover": {
                                                bgcolor: viewMode === "chart" ? "var(--color-primary-dark)" : "var(--color-surface-hover)",
                                                borderColor: "var(--color-border-secondary)",
                                            },
                                            textTransform: "none",
                                            fontSize: "var(--font-size-xs)",
                                        }}
                                    >
                                        <Activity size={14} style={{ marginRight: 4 }} />
                                        Chart
                                    </Button>
                                )}
                            </ButtonGroup>

                            <Tooltip title="Export to Excel">
                                <IconButton
                                    size="small"
                                    onClick={exportToExcel}
                                    disabled={rows.length === 0}
                                    sx={{
                                        border: "1px solid var(--color-border-primary)",
                                        color: "var(--color-text-muted)",
                                        "&:hover": {
                                            backgroundColor: "var(--color-surface-hover)",
                                            borderColor: "var(--color-border-secondary)",
                                            color: "var(--color-text-secondary)",
                                        },
                                        "&.Mui-disabled": {
                                            color: "var(--color-text-muted)",
                                            opacity: 0.5,
                                            borderColor: "var(--color-border-primary)",
                                        },
                                    }}
                                >
                                    <Download size={18} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Tabs */}
                    <Tabs
                        value={tab}
                        onChange={(_, v) => { setTab(v); if (v === 3) setViewMode("table"); }}
                        sx={{
                            minHeight: 44,
                            "& .MuiTabs-indicator": { height: 2, backgroundColor: "var(--color-primary)" },
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "var(--font-size-sm)",
                                minHeight: 44,
                                px: 2.5,
                                color: "var(--color-text-muted)",
                                "&.Mui-selected": { color: "var(--color-text-primary)" },
                                "&:hover": { color: "var(--color-text-secondary)" },
                            },
                        }}
                    >
                        <Tab label="Income Statement" />
                        <Tab label="Balance Sheet" />
                        <Tab label="Cash Flow" />
                        <Tab label="Ratios" />
                    </Tabs>
                </Box>

                {/* ── Controls ── */}
                <Box sx={{
                    px: { xs: 2, sm: 3 }, py: 2.5,
                    backgroundColor: "var(--color-surface)",
                    borderBottom: "1px solid var(--color-border-primary)",
                    display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap",
                }}>
                    {tab !== 3 && (
                        <>
                            <FormControl size="small" sx={{
                                minWidth: 140,
                                "& .MuiInputLabel-root": { color: "var(--color-text-muted)", fontSize: "var(--font-size-base) !important" },
                                "& .MuiInputLabel-root.MuiInputLabel-shrink": { fontSize: "var(--font-size-base) !important" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "var(--color-primary)" },
                            }}>
                                <InputLabel>Period</InputLabel>
                                <Select
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value)}
                                    label="Period"
                                    startAdornment={<Calendar size={16} style={{ marginRight: 8, color: "var(--color-primary)" }} />}
                                    sx={{
                                        color: "var(--color-text-secondary)",
                                        fontSize: "var(--font-size-sm)",
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-primary)" },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-secondary)" },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-primary)" },
                                        "& .MuiSelect-icon": { color: "var(--color-text-muted)" },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: "var(--color-bg-secondary)",
                                                border: "1px solid var(--color-border-primary)",
                                                "& .MuiMenuItem-root": {
                                                    color: "var(--color-text-secondary)",
                                                    fontSize: "var(--font-size-sm)",
                                                    "&:hover": { bgcolor: "var(--color-surface-hover)" },
                                                    "&.Mui-selected": { bgcolor: "var(--color-surface-hover)", "&:hover": { bgcolor: "var(--color-surface-hover)" } },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="annual">Annual</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{
                                minWidth: 160,
                                "& .MuiInputLabel-root": { color: "var(--color-text-muted)", fontSize: "var(--font-size-base) !important" },
                                "& .MuiInputLabel-root.MuiInputLabel-shrink": { fontSize: "var(--font-size-base) !important" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "var(--color-primary)" },
                            }}>
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    label="Sort Order"
                                    sx={{
                                        color: "var(--color-text-secondary)",
                                        fontSize: "var(--font-size-sm)",
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-primary)" },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-secondary)" },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-primary)" },
                                        "& .MuiSelect-icon": { color: "var(--color-text-muted)" },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: "var(--color-bg-secondary)",
                                                border: "1px solid var(--color-border-primary)",
                                                "& .MuiMenuItem-root": {
                                                    color: "var(--color-text-secondary)",
                                                    fontSize: "var(--font-size-sm)",
                                                    "&:hover": { bgcolor: "var(--color-surface-hover)" },
                                                    "&.Mui-selected": { bgcolor: "var(--color-surface-hover)", "&:hover": { bgcolor: "var(--color-surface-hover)" } },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="desc">Latest Left</MenuItem>
                                    <MenuItem value="asc">Latest Right</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {tab === 3 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" fontWeight={600} mb={1}>
                                CATEGORY
                            </Typography>
                            <Select
                                value={selectedCategoryId}
                                displayEmpty
                                onChange={(e) => setSelectedCategoryId(e.target.value)}
                                sx={{
                                    fontSize: "var(--font-size-sm)",
                                    bgcolor: "var(--color-surface)",
                                    color: "var(--color-text-secondary)",
                                    borderRadius: "var(--radius-lg)",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-primary)" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-secondary)" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-primary)", borderWidth: "2px" },
                                    "& .MuiSvgIcon-root": { color: "var(--color-text-muted)" },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: "var(--color-bg-secondary)",
                                            backdropFilter: "blur(20px)",
                                            border: "1px solid var(--color-border-primary)",
                                            borderRadius: "var(--radius-lg)",
                                            mt: 0.5,
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                        All Categories
                                    </Typography>
                                </MenuItem>
                                {ratioCategories.map(cat => (
                                    <MenuItem
                                        key={cat.id}
                                        value={cat.id}
                                        sx={{
                                            fontSize: "var(--font-size-sm)",
                                            color: "var(--color-text-secondary)",
                                            py: 1.5,
                                            "&:hover": { bgcolor: "var(--color-surface-hover)" },
                                        }}
                                    >
                                        {cat.category_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {/* ── Content ── */}
                <Box sx={{
                    overflowX: "auto",
                    minHeight: 400,
                    backgroundColor: "var(--color-surface)",
                    "&::-webkit-scrollbar": { height: "12px" },
                    "&::-webkit-scrollbar-track": { background: "var(--color-bg-secondary)", borderRadius: "6px", margin: "0 8px" },
                    "&::-webkit-scrollbar-thumb": {
                        background: "linear-gradient(to right, var(--color-primary-dark), var(--color-primary))",
                        borderRadius: "6px",
                        border: "2px solid var(--color-bg-secondary)",
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "var(--color-primary) var(--color-bg-secondary)",
                }}>
                    {loading ? (
                        <TableSkeleton />
                    ) : rows.length === 0 ? (
                        <Box sx={{ py: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Info size={48} style={{ color: "var(--color-text-muted)" }} />
                            <Typography color="var(--color-text-secondary)" fontWeight={500} fontSize="var(--font-size-sm)">
                                No financial data available
                            </Typography>
                            <Typography variant="body2" color="var(--color-text-muted)" fontSize="var(--font-size-xs)">
                                Try selecting a different period or tab
                            </Typography>
                        </Box>
                    ) : viewMode === "chart" && tab !== 3 ? (

                        /* ── Chart View ── */
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ width: 16, height: 3, bgcolor: "var(--color-primary)", borderRadius: 1 }} />
                                    <Typography variant="caption" color="var(--color-text-muted)" fontSize="var(--font-size-xs)">Historical Data</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ width: 16, height: 3, bgcolor: "#a78bfa", borderRadius: 1, border: "1px dashed rgba(167,139,250,0.5)" }} />
                                    <Typography variant="caption" color="var(--color-text-muted)" fontSize="var(--font-size-xs)">Estimated / Forecast</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ width: 2, height: 24, bgcolor: "#f59e0b" }} />
                                    <Typography variant="caption" color="var(--color-text-muted)" fontSize="var(--font-size-xs)">
                                        Current Year ({currentYearLabel})
                                    </Typography>
                                </Box>
                            </Box>

                            <ResponsiveContainer width="100%" height={450}>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" />
                                    <XAxis
                                        dataKey="period"
                                        stroke="var(--color-text-muted)"
                                        style={{ fontSize: "var(--font-size-xs)", fill: "var(--color-text-muted)" }}
                                    />
                                    <YAxis
                                        stroke="var(--color-text-muted)"
                                        style={{ fontSize: "var(--font-size-xs)", fill: "var(--color-text-muted)" }}
                                        // ✅ YAxis tick also uses en-US comma format
                                        tickFormatter={(v) =>
                                            Number(v).toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 2 })
                                        }
                                        label={{
                                            value: tab === 3 ? "Percentage (%)" : `Amount (${unitNote})`,
                                            angle: -90,
                                            position: "insideLeft",
                                            style: { fill: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" },
                                        }}
                                    />
                                    <RechartsTooltip
                                        contentStyle={{
                                            backgroundColor: "var(--color-bg-secondary)",
                                            border: "1px solid var(--color-border-primary)",
                                            borderRadius: "var(--radius-md)",
                                            color: "var(--color-text-secondary)",
                                        }}
                                        formatter={(value, name) => {
                                            if (value === undefined || value === null) return ["—", prettifyKey(name)];
                                            if (tab === 3 || isPercentField(name)) {
                                                return [
                                                    // ✅ Chart tooltip — en-US comma separators
                                                    `${Number(value).toLocaleString(NUMBER_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
                                                    prettifyKey(name),
                                                ];
                                            }
                                            return [
                                                // ✅ Chart tooltip — en-US comma separators
                                                Number(value).toLocaleString(NUMBER_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                                                prettifyKey(name),
                                            ];
                                        }}
                                    />
                                    <Legend
                                        wrapperStyle={{ paddingTop: "20px", color: "var(--color-text-secondary)" }}
                                        iconType="line"
                                        formatter={(value) => prettifyKey(value)}
                                    />
                                    <ReferenceLine
                                        x={currentPoint?.period}
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        label={{ value: "Current", position: "top", fill: "#f59e0b", fontSize: "var(--font-size-xs)", fontWeight: 600 }}
                                    />
                                    {activeMetrics.map((metric, index) => {
                                        if (!chartData.some(d => d[metric] !== undefined)) return null;
                                        return (
                                            <Line
                                                key={metric}
                                                type="monotone"
                                                dataKey={metric}
                                                stroke={getMetricColor(metric, index)}
                                                strokeWidth={2.5}
                                                name={metric}
                                                dot={(props) => {
                                                    const { cx, cy, payload } = props;
                                                    return (
                                                        <circle
                                                            cx={cx} cy={cy} r={5}
                                                            fill={payload.isEstimate ? "#a78bfa" : getMetricColor(metric, index)}
                                                            stroke={payload.isCurrent ? "#f59e0b" : "#0f172a"}
                                                            strokeWidth={payload.isCurrent ? 3 : 2}
                                                        />
                                                    );
                                                }}
                                                strokeDasharray={(props) => {
                                                    const point = chartData[props.index];
                                                    return point?.isEstimate ? "8 4" : "0";
                                                }}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>

                            <Box sx={{ mt: 3, p: 2, bgcolor: "var(--color-bg-secondary)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border-primary)" }}>
                                <Typography variant="body2" color="var(--color-text-muted)" fontSize="var(--font-size-xs)" sx={{ mb: 1 }}>
                                    <strong style={{ color: "var(--color-text-secondary)" }}>Trend Analysis:</strong>
                                </Typography>
                                <Typography variant="body2" color="var(--color-text-muted)" fontSize="var(--font-size-xs)" lineHeight={1.6}>
                                    {tab === 0 && "The chart displays revenue and profitability metrics over time. "}
                                    {tab === 1 && "The chart shows the company's financial position including assets, liabilities, and equity. "}
                                    {tab === 2 && "The chart illustrates cash flow from operations, investing, and financing activities. "}
                                    {tab === 3 && "The chart presents key financial ratios as percentages. "}
                                    Historical performance is shown with solid lines, transitioning to forecasted estimates with dashed lines
                                    beyond the current year ({currentYearLabel}, marked with orange line). Estimated values are indicated with
                                    purple dots and represent projected trends based on historical data patterns.
                                </Typography>
                            </Box>
                        </Box>

                    ) : (

                        /* ── Table View ── */
                        <Box
                            component="table"
                            sx={{
                                width: "100%",
                                tableLayout: tab === 3 ? "fixed" : "auto",
                                borderCollapse: "separate",
                                borderSpacing: 0,
                            }}
                        >
                            <Box component="thead" sx={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <Box component="tr">
                                    <Box
                                        component="th"
                                        sx={{
                                            position: "sticky", left: 0,
                                            backgroundColor: "var(--color-bg-secondary)",
                                            zIndex: 2, p: 2,
                                            textAlign: "left",
                                            fontWeight: 600,
                                            fontSize: "var(--font-size-xs)",
                                            color: "var(--color-text-muted)",
                                            borderBottom: "1px solid var(--color-border-primary)",
                                            borderRight: "1px solid var(--color-border-primary)",
                                            width: tab === 3 ? "35%" : 200,
                                            minWidth: tab === 3 ? 120 : 200,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.03em",
                                        }}
                                    >
                                        Metric
                                    </Box>
                                    {columns.map((c, idx) => (
                                        <Box
                                            component="th"
                                            key={c}
                                            sx={{
                                                p: 2,
                                                fontSize: "var(--font-size-xs)",
                                                fontWeight: 600,
                                                textAlign: tab === 3 ? "center" : "right",
                                                whiteSpace: "nowrap",
                                                color: columnTypes[idx] === "estimate" ? "var(--color-text-primary)" : "var(--color-text-muted)",
                                                borderBottom: "1px solid var(--color-border-primary)",
                                                minWidth: tab === 3 ? 90 : 130,
                                                backgroundColor:
                                                    columnTypes[idx] === "current" ? "rgba(0,150,255,0.15)"
                                                        : columnTypes[idx] === "estimate" ? "rgba(167,139,250,0.1)"
                                                            : "var(--color-bg-secondary)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.03em",
                                                position: "relative",
                                                "&::before": columnTypes[idx] === "estimate" ? {
                                                    content: '""',
                                                    position: "absolute", top: 0, left: 0, right: 0, height: "2px",
                                                    background: "linear-gradient(to right, transparent, rgba(167,139,250,0.6), transparent)",
                                                } : {},
                                            }}
                                        >
                                            {c}
                                            {columnTypes[idx] === "current" && (
                                                <Chip label="Current" size="small" sx={{ ml: 1, height: 20, fontSize: "var(--font-size-xs)", fontWeight: 600, bgcolor: "var(--color-primary)", color: "#ffffff" }} />
                                            )}
                                            {columnTypes[idx] === "estimate" && (
                                                <Chip label="Est." size="small" sx={{ ml: 1, height: 20, fontSize: "var(--font-size-xs)", fontWeight: 600, bgcolor: "#a78bfa", color: "#ffffff" }} />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box component="tbody">
                                {rows.map((row, i) => {
                                    const currentIdx = columnTypes.findIndex(t => t === "current");
                                    const change =
                                        currentIdx >= 0 && currentIdx < row.values.length - 1
                                            ? calculateChange(row.values[currentIdx], row.values[currentIdx + 1])
                                            : null;

                                    return (
                                        <Box
                                            component="tr"
                                            key={i}
                                            sx={{
                                                "&:hover": {
                                                    "& td": { backgroundColor: "var(--color-surface-hover)" },
                                                    "& td:first-of-type": {
                                                        backgroundColor: row.isHighlighted
                                                            ? "var(--color-primary-lighter)"
                                                            : "var(--color-surface-hover)",
                                                    },
                                                },
                                                transition: "var(--transition-fast)",
                                            }}
                                        >
                                            <Box
                                                component="td"
                                                sx={{
                                                    position: "sticky", left: 0, p: 2,
                                                    fontSize: "var(--font-size-sm)",
                                                    fontWeight: row.isHighlighted ? 700 : 500,
                                                    backgroundColor: row.isHighlighted ? "var(--color-primary-lighter)" : "var(--color-surface)",
                                                    color: row.isHighlighted ? "var(--color-primary)" : "var(--color-text-secondary)",
                                                    borderLeft: row.isHighlighted ? "3px solid var(--color-primary)" : "none",
                                                    borderBottom: "1px solid var(--color-border-primary)",
                                                    borderRight: "1px solid var(--color-border-primary)",
                                                    zIndex: 2,
                                                    width: tab === 3 ? "35%" : 200,
                                                    minWidth: tab === 3 ? 120 : 200,
                                                }}
                                            >
                                                {row.label}
                                            </Box>

                                            {row.values.map((v, j) => (
                                                <Box
                                                    component="td"
                                                    key={j}
                                                    sx={{
                                                        p: 2,
                                                        fontSize: "var(--font-size-sm)",
                                                        textAlign: tab === 3 ? "center" : "right",
                                                        fontWeight: columnTypes[j] === "current" ? 500 : 400,
                                                        color:
                                                            columnTypes[j] === "estimate" ? "var(--color-text-primary)"
                                                                : columnTypes[j] === "current" ? "var(--color-text-secondary)"
                                                                    : "var(--color-text-muted)",
                                                        borderBottom: "1px solid var(--color-border-primary)",
                                                        backgroundColor: row.isHighlighted
                                                            ? "var(--color-primary-lighter)"
                                                            : columnTypes[j] === "current" ? "var(--color-bg-secondary)" : "inherit",
                                                        fontStyle: columnTypes[j] === "estimate" ? "italic" : "normal",
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: "flex", alignItems: "center",
                                                        justifyContent: tab === 3 ? "center" : "flex-end",
                                                        gap: 1,
                                                    }}>
                                                        {v}
                                                        {columnTypes[j] === "current" && change !== null && !row.isPercent && (
                                                            <Chip
                                                                icon={change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                                                label={`${change >= 0 ? "+" : ""}${change.toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 1 })}%`}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: "var(--font-size-xs)",
                                                                    fontWeight: 600,
                                                                    bgcolor: change >= 0 ? "#10b981" : "#ef4444",
                                                                    color: "#ffffff",
                                                                    "& .MuiChip-icon": { fontSize: 12, color: "#ffffff" },
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default Financials;